import { Link, useNavigate } from "@remix-run/react";
import { isValidCertificateCodeShape, normalizeCertificateCode } from "@repo/shared";
import { useState } from "react";

import { buildMeta, getCompanyFromMatches } from "~/utils/meta-helpers";

import { AcademiaSeal } from "../components/CuadernoVivo/AcademiaSeal";
import { WashiTape } from "../components/CuadernoVivo/WashiTape";

import type { MetaFunction } from "@remix-run/node";

export const meta: MetaFunction = ({ matches }) => {
  const company = getCompanyFromMatches(matches);
  return buildMeta({
    title: `Verificar una credencial | ${company}`,
    description:
      "Ingresá el código de una constancia o certificado de AcademIA y comprobá si figura en nuestros registros.",
  });
};

/** El máximo que acepta el endpoint público; más que eso no puede ser un código. */
const MAX_TYPED_CODE_LENGTH = 32;

/**
 * /verificar — la puerta de entrada cuando alguien tiene el código pero no el QR.
 *
 * El resultado NO se muestra acá: el formulario navega a /certificado/:code, que es la
 * misma url que imprime el QR. Así hay un solo lugar donde se dicta un veredicto, y ese
 * veredicto tiene url propia — se puede compartir, guardar en favoritos y volver con el
 * botón de atrás, cosas que un resultado embebido en el formulario no permite.
 */
export default function VerifyPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");

  // Sólo habilita el botón. El veredicto lo da el servidor: cualquier texto de doce
  // caracteres tiene forma de código, así que la forma no prueba absolutamente nada.
  const canSubmit = isValidCertificateCodeShape(code);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const canonical = normalizeCertificateCode(code);
    if (!canonical) return;

    navigate(`/certificado/${canonical}`);
  };

  return (
    <section className="landing-grain relative bg-landing-cream/70 px-5 py-16 backdrop-blur-[1px] sm:px-8 sm:py-20">
      <div className="relative mx-auto max-w-[560px]">
        <div className="text-center">
          <span className="landing-eyebrow block font-bold text-landing-teal">
            Verificación de credenciales
          </span>
          <h1 className="font-display mt-3 text-balance text-[clamp(26px,3.6vw,36px)] font-extrabold leading-tight tracking-tight text-landing-text">
            Comprobá una credencial de AcademIA
          </h1>
          <p className="mx-auto mt-2 max-w-[46ch] text-[14.5px] leading-relaxed text-landing-muted">
            Ingresá el código que figura en la constancia o el certificado. Te decimos si está en
            nuestros registros, a nombre de quién y de qué curso.
          </p>
        </div>

        <div className="relative mt-9 rounded-[4px] bg-landing-paper p-7 shadow-landing-xl sm:p-9">
          <WashiTape variant={0} />
          <AcademiaSeal size={52} className="absolute -top-3 right-5 rotate-[8deg]" />

          <form onSubmit={handleSubmit} className="grid gap-3">
            <label htmlFor="code" className="text-[13px] font-semibold text-landing-text">
              Código de la credencial
            </label>
            <input
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="A1B2·C3D4·E5F6"
              maxLength={MAX_TYPED_CODE_LENGTH}
              autoComplete="off"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              aria-describedby="code-hint"
              className="w-full rounded-[8px] border-[1.5px] border-landing-border bg-landing-white px-4 py-3 text-[16px] text-landing-text outline-none transition-colors [font-variant-numeric:tabular-nums] placeholder:text-landing-light focus:border-landing-teal focus:ring-1 focus:ring-landing-teal/30"
            />
            <p id="code-hint" className="text-[12.5px] leading-relaxed text-landing-muted">
              Son 12 caracteres. Podés pegarlo tal cual del documento: los puntos, los espacios y
              las minúsculas no molestan.
            </p>
            <button
              type="submit"
              disabled={!canSubmit}
              data-cuelume-press
              className="mt-1 w-full rounded-[10px] bg-landing-red px-6 py-3.5 text-[15px] font-bold text-white shadow-landing-red transition-all hover:-translate-y-0.5 hover:bg-landing-red-hover disabled:translate-y-0 disabled:opacity-50 disabled:shadow-none"
            >
              Verificar
            </button>
          </form>

          {/* Sustituye al botón de cámara que había acá. El QR guarda una url común y
              corriente, y la cámara del teléfono la abre sola: un lector propio pediría
              permisos, muestrearía cuadros y fallaría con poca luz para hacer peor lo que
              el celular ya hace bien. */}
          <p className="mt-6 border-t-[1.5px] border-dashed border-landing-border pt-5 text-[13px] leading-relaxed text-landing-muted">
            <b className="text-landing-text">¿Tenés el QR a mano?</b> Apuntale la cámara del
            celular. Te abre esta misma verificación sin tipear nada.
          </p>
        </div>

        <p className="mt-6 text-center text-[12.5px] leading-relaxed text-landing-muted">
          Lo que se muestra al verificar no sale del documento: sale de nuestros registros. Un papel
          bien imitado no cambia lo que figura en esa página.{" "}
          <Link
            to="/certificado"
            data-cuelume-hover="tick"
            className="font-semibold text-landing-teal underline decoration-landing-teal/40 underline-offset-4 hover:decoration-landing-teal"
          >
            Ver un ejemplo
          </Link>
        </p>
      </div>
    </section>
  );
}
